/**
 * API Helper utilities for consistent response handling
 * Used with React Query and standard API responses
 */

/**
 * Creates a selector function for React Query to extract data from API responses
 * @param {string} dataKey - The key to extract from response.data
 * @param {*} defaultValue - Default value if key doesn't exist
 * @returns {Function} Selector function for React Query
 */
export const createResponseSelector = (dataKey, defaultValue = null) => {
    return (response) => {
        // Handle different response structures
        if (response?.data?.success && response?.data?.data) {
            // Standard API response format: { success: true, message: "...", data: {...} }
            if (dataKey) {
                return response.data.data[dataKey] || defaultValue;
            }
            return response.data.data;
        }

        // Fallback for direct data responses
        if (response?.data) {
            if (dataKey && typeof response.data === 'object') {
                return response.data[dataKey] || defaultValue;
            }
            return response.data;
        }

        return defaultValue;
    };
};

/**
 * Extracts data from API response
 * @param {Object} response - API response object
 * @param {string} dataKey - Optional key to extract from response.data
 * @returns {*} Extracted data or null
 */
export const extractResponseData = (response, dataKey = null) => {
    if (response?.success && response?.data) {
        // Direct response object (already extracted from axios)
        if (dataKey) {
            return response.data[dataKey] || null;
        }
        return response.data;
    }

    if (response?.data?.success && response?.data?.data) {
        // Standard API response format from axios
        if (dataKey) {
            return response.data.data[dataKey] || null;
        }
        return response.data.data;
    }

    // Fallback for other response formats
    if (response?.data) {
        if (dataKey && typeof response.data === 'object') {
            return response.data[dataKey] || null;
        }
        return response.data;
    }

    return null;
};

/**
 * Extracts error message from API response or error object
 * @param {Object} error - Error object from API call
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string} Error message
 */
export const extractErrorMessage = (error, defaultMessage = 'An error occurred') => {
    // Check for Axios error response
    if (error?.response?.data) {
        const errorData = error.response.data;

        // Standard error response format
        if (errorData.message) {
            return errorData.message;
        }

        // Validation errors
        if (errorData.errors) {
            if (Array.isArray(errorData.errors)) {
                return errorData.errors.join(', ');
            }
            if (typeof errorData.errors === 'object') {
                return Object.values(errorData.errors).join(', ');
            }
        }

        // Direct error message
        if (typeof errorData === 'string') {
            return errorData;
        }
    }

    // Check for direct error message
    if (error?.message) {
        return error.message;
    }

    // Network or other errors
    if (error?.code === 'NETWORK_ERROR') {
        return 'Network error. Please check your connection.';
    }

    if (error?.code === 'ECONNABORTED') {
        return 'Request timeout. Please try again.';
    }

    return defaultMessage;
};

/**
 * Extracts success message from API response
 * @param {Object} response - API response object
 * @param {string} defaultMessage - Default message if extraction fails
 * @returns {string} Success message
 */
export const extractSuccessMessage = (response, defaultMessage = 'Operation successful') => {
    // Check for standard API response format
    if (response?.data?.success && response?.data?.message) {
        return response.data.message;
    }

    // Check for direct response object (already extracted)
    if (response?.success && response?.message) {
        return response.message;
    }

    // Fallback to message field
    if (response?.message) {
        return response.message;
    }

    return defaultMessage;
};

/**
 * Checks if API response indicates success
 * @param {Object} response - API response object
 * @returns {boolean} True if successful
 */
export const isSuccessResponse = (response) => {
    // Standard API response format
    if (response?.data?.success !== undefined) {
        return response.data.success;
    }

    // Direct response object
    if (response?.success !== undefined) {
        return response.success;
    }

    // HTTP status based check
    if (response?.status) {
        return response.status >= 200 && response.status < 300;
    }

    return false;
};

/**
 * Extracts pagination info from API response
 * @param {Object} response - API response object
 * @returns {Object} Pagination info object
 */
export const extractPaginationInfo = (response) => {
    const defaultPagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false
    };

    let data = null;

    // Extract data from different response formats
    if (response?.data?.success && response?.data?.data) {
        data = response.data.data;
    } else if (response?.data) {
        data = response.data;
    } else {
        data = response;
    }

    if (data && typeof data === 'object') {
        return {
            page: data.page || data.currentPage || defaultPagination.page,
            limit: data.limit || data.pageSize || defaultPagination.limit,
            total: data.total || data.totalCount || defaultPagination.total,
            totalPages: data.totalPages || Math.ceil((data.total || 0) / (data.limit || 10)),
            hasNextPage: data.hasNextPage || false,
            hasPrevPage: data.hasPrevPage || false
        };
    }

    return defaultPagination;
};

/**
 * Creates a standardized error object for consistent error handling
 * @param {*} error - Original error
 * @param {string} context - Context where error occurred
 * @returns {Object} Standardized error object
 */
export const createStandardError = (error, context = '') => {
    return {
        message: extractErrorMessage(error),
        context,
        originalError: error,
        timestamp: new Date().toISOString()
    };
};
